import { useNavigate } from "react-router-dom";
import { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { useFetchUserData } from "app/hook";
import { LocalStorageKey } from "app/types";
import { AppDispatch, RootState } from "app/store";
import I18n from "common/i18n";
import { Loading } from "common/elements";
import { IconArrowLeft, IconArrowRight } from "common/assets";
import { btnDelay, useScale, usePlaySound } from "common/utils";
import { actions } from "features/user/slice";
import { TitleSvg } from "features/home/assets";
import { usePlan } from "features/subscription/hooks";
import { PlanType } from "features/subscription/types";
import { ParentGateButton } from "features/parent/ParentGateButton";
import { AppUser, UserType, AccountType } from "features/user/types";
import { UserList } from "./UserList";

export const UserSelection = () => {
  const TITLE_WIDTH = 828;
  const TITLE_HEIGHT = 182;
  const CARD_SIZE = 390;
  const CARD_GAP = 16;
  const VISIBLE_PLAYERS = 2;
  const play = usePlaySound();
  const plan = usePlan();
  const { scale } = useScale();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch<AppDispatch>();
  const [scroll, setScroll] = useState(false);
  const [loading, setLoading] = useState(false);
  const appLoading = useSelector((state: RootState) => state.config.loading);
  const lastActiveUserUid = localStorage.getItem(LocalStorageKey.LAST_ACTIVE_USER_UID);
  const { handleFetchUserData } = useFetchUserData();
  const users = useSelector((state: RootState) => state.user.users);
  const [showUserListDialog, setShowUserListDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const children = users
    .filter((user) => user.account_type !== AccountType.PARENTS && user.type !== UserType.FROZEN)
    .sort((a, b) => {
      if (a.uid === lastActiveUserUid) {
        return -1;
      }
      if (b.uid === lastActiveUserUid) {
        return 1;
      }
      return dayjs(a.last_sign_in_time) > dayjs(b.last_sign_in_time) ? -1 : 1;
    });

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(children.length > VISIBLE_PLAYERS);

  useEffect(() => {
    if (children.length > 0 && !selectedUser) {
      setSelectedUser(children[0]);
    }
  }, [children, selectedUser]);

  // スクロール可能状態を更新
  const updateScrollState = (element: HTMLDivElement) => {
    const maxScroll = element.scrollWidth - element.clientWidth;
    setCanScrollLeft(element.scrollLeft > 1);
    setCanScrollRight(element.scrollLeft < maxScroll - 1);
  };

  // 初期状態の更新
  useEffect(() => {
    if (ref.current && children.length > VISIBLE_PLAYERS) {
      updateScrollState(ref.current);
    }
  }, [children.length]);

  // スクロール完了後にボタン状態を更新
  useEffect(() => {
    const element = ref.current;
    if (!element || children.length <= VISIBLE_PLAYERS) return;

    const handleScrollEnd = () => {
      updateScrollState(element);
      setScroll(false);
    };

    const supportsScrollEnd = "onscrollend" in window;

    if (supportsScrollEnd) {
      element.addEventListener("scrollend", handleScrollEnd);
      return () => element.removeEventListener("scrollend", handleScrollEnd);
    } else {
      let scrollEndTimer: ReturnType<typeof setTimeout>;
      const handleScroll = () => {
        clearTimeout(scrollEndTimer);
        scrollEndTimer = setTimeout(handleScrollEnd, 150);
      };
      element.addEventListener("scroll", handleScroll);
      return () => {
        element.removeEventListener("scroll", handleScroll);
        clearTimeout(scrollEndTimer);
      };
    }
  }, [children.length]);

  const handleUserSelect = (user: AppUser) => () => {
    btnDelay(() => {
      play();
      setShowUserListDialog(false);
      setLoading(true);
      handleFetchUserData({ parent_uid: user.parent_uid, uid: user.uid })
        .then(() => {
          dispatch(actions.setActiveUser(user));
          navigate("/top", { replace: true });
        })
        .catch((error) => {
          setLoading(false);
          console.error(error);
        });
    });
  };

  const handleScrollBtn = (direction: number) => () => {
    if (scroll || !ref.current) return;
    play();
    setScroll(true);

    const scrollAmount = (CARD_SIZE + CARD_GAP) * 2; // 2人分
    ref.current.scrollLeft += direction * scrollAmount;
  };

  return (
    <>
      {(loading || appLoading) && <Loading />}
      <div className="flex-col-el flex-center relative w-full h-full gap-4">
        {children.length > 0 && (
          <>
            <div
              className="flex-col-view self-center"
              style={{
                width: TITLE_WIDTH * scale,
                height: TITLE_HEIGHT * scale,
              }}
            >
              <div
                className="flex-col-view origin-top-left"
                style={{
                  width: TITLE_WIDTH,
                  height: TITLE_HEIGHT,
                  transform: `scale(${scale})`,
                }}
              >
                <TitleSvg />
              </div>
            </div>

            <div className="flex-col-center w-full">
              <div className="flex-col-el flex-center w-full">
                <div
                  style={{ width: 1000 * scale, height: 500 * scale }}
                  className="flex-row-el flex-center p-4"
                >
                  <div
                    style={{ transform: `scale(${scale})` }}
                    className="flex-col-el flex-center w-[1000px] h-[500px]"
                  >
                    <div className="flex-row-center px-4 py-2 bg-orange-alt rounded-full z-10">
                      <span className="material-symbols-outlined !text-[24px] text-textcolor-black">
                        group
                      </span>
                      <p className="text-body text-textcolor-black text-2xl !px-[8px]">
                        {I18n.t("MSG_USER_SELECTION_PROMPT")}
                      </p>
                    </div>

                    <div
                      style={{
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        boxShadow: "0px 1px 3px rgba(63, 74, 97, 0.3)",
                        marginTop: -30,
                      }}
                      className="flex-row-el flex-center backdrop-blur-[4px] w-full p-5 border-solid border-beige-alt border-8 rounded-[12px] gap-2"
                    >
                      {children.length > VISIBLE_PLAYERS && (
                        <div className="flex-row-el flex-center shrink-0 upsilon z-50 cursor-pointer pointer-events-auto">
                          <button
                            onClick={handleScrollBtn(-1)}
                            disabled={!canScrollLeft}
                            className={`btn btn-primary w-[40px] h-[72px] ${!canScrollLeft ? "opacity-30 cursor-not-allowed" : ""}`}
                          >
                            <div className="flex-col-center">
                              <IconArrowLeft />
                            </div>
                          </button>
                        </div>
                      )}

                      <div
                        ref={ref}
                        className="flex-row-el item-center justify-start overflow-x-auto gap-4 p-4 invisible-scrollbar"
                        style={{
                          maxWidth: TITLE_WIDTH,
                        }}
                      >
                        {children.map((user, index) => (
                          <div
                            key={`${user.uid}-${index}`}
                            onClick={handleUserSelect(user)}
                            style={{
                              width: CARD_SIZE,
                              height: CARD_SIZE,
                            }}
                            className="flex-row-el flex-center cursor-pointer shrink-0"
                          >
                            <div className="flex-row-el flex-center w-[390px] h-[390px] cursor-pointer">
                              <div
                                className="flex-col-el flex-center relative w-[312px] h-[312px] bg-white rounded-full z-10 active:shadow-active-show active:scale-95 transition-all duration-300"
                                style={{
                                  filter: "drop-shadow(1px 1px 2px rgba(63, 74, 97, 0.4))",
                                }}
                              >
                                <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden">
                                  <img
                                    key={user.icon}
                                    alt="プレイヤープロフィール画像"
                                    src={user.icon}
                                    className="w-full h-full object-contain"
                                  />
                                </div>

                                <div
                                  className="flex-col-el flex-center absolute -bottom-3 w-[312px] h-[60px] px-2 bg-beige-10 rounded-[32px] text-textcolor-black"
                                  style={{
                                    boxShadow:
                                      "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                                  }}
                                >
                                  <p className="text-body text-3xl text-center text-body-ellipsis w-full">
                                    {user.display_name}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {children.length > VISIBLE_PLAYERS && (
                        <div className="flex-row-el flex-center shrink-0 upsilon z-50 cursor-pointer pointer-events-auto">
                          <button
                            onClick={handleScrollBtn(1)}
                            disabled={!canScrollRight}
                            className={`btn btn-primary w-[40px] h-[72px] ${!canScrollRight ? "opacity-30 cursor-not-allowed" : ""}`}
                          >
                            <div className="flex-col-center">
                              <IconArrowRight />
                            </div>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {children.length > VISIBLE_PLAYERS && plan === PlanType.EDUCATION && (
                  <div
                    style={{ width: 380 * scale, height: 120 * scale }}
                    className="flex-row-el flex-center upsilon"
                  >
                    <div
                      onClick={() => setShowUserListDialog(true)}
                      style={{ transform: `scale(${scale})` }}
                      className="btn btn-primary flex-row-center relative rounded-2xl cursor-pointer"
                    >
                      <div className="flex-row-el flex-center py-2 w-full h-full">
                        <span className="material-symbols-outlined !text-xl text-white">
                          expand_circle_down
                        </span>

                        <div className="flex-col-el px-1">
                          <p className="text-body text-center text-white text-base w-full text-body-ellipsis">
                            {I18n.t("MSG_USER_LIST")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <UserList
          visible={showUserListDialog}
          close={() => setShowUserListDialog(false)}
          children={children}
          lastActiveUserUid={lastActiveUserUid}
          onUserSelect={handleUserSelect}
        />

        <ParentGateButton />
      </div>
    </>
  );
};
